import type { GarageAccount } from '@parkingbase/ecommerce-api-dev';

/**
 * Whether the customer may be offered an Introductory Rate at a garage.
 *
 * `unknown` is distinct from `ineligible` on purpose: it means we do not yet hold the data to
 * decide, so the rate must be withheld rather than guessed at in either direction.
 */
export type IntroRateEligibility = 'eligible' | 'ineligible' | 'unknown';

/** Permit statuses that never represented a held permit, so they are not history. */
const NEVER_ACTIVE_STATUSES = new Set(['PENDING', 'QUOTE', 'WAITLIST']);

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface EligibilityContext {
  /** Signed-in state. Anonymous visitors are always offered intro rates (FR-3). */
  isAuthenticated: boolean;
  /** True once the garage-accounts fetch has resolved at least once. */
  hasLoadedGarageAccounts: boolean;
  /**
   * True when that fetch failed. Load-bearing: on failure `garageAccounts` is empty for a
   * reason that has nothing to do with the customer, and reading it as "no history" hands out
   * the promo to everyone.
   */
  garageAccountsLoadFailed?: boolean;
  garageAccounts: GarageAccount[];
}

/**
 * Latest end date, in ms, of any permit this customer has held at the garage. Returns null when
 * they have no history there.
 *
 * ## Invariants:
 * - A permit that is still running counts, and its end date is in the future, which makes the
 *   day difference negative and therefore always short of any threshold.
 *   Why: that is what stops someone re-buying the promo the day after they bought it.
 * - PENDING / QUOTE / WAITLIST permits are ignored — they were never held.
 */
function latestPermitEndAtGarage(garageId: string, garageAccounts: GarageAccount[]): number | null {
  let latest: number | null = null;
  for (const account of garageAccounts) {
    if (!account || account.garageId !== garageId) continue;
    const permits = account.monthlyAccount?.monthlyPermits ?? [];
    for (const permit of permits) {
      const status = permit?.status ? String(permit.status).toUpperCase() : '';
      if (NEVER_ACTIVE_STATUSES.has(status)) continue;
      if (!permit?.endDate) continue;
      const end = new Date(permit.endDate).getTime();
      if (Number.isNaN(end)) continue;
      if (latest === null || end > latest) latest = end;
    }
  }
  return latest;
}

/**
 * Resolves Introductory Rate eligibility from the garage accounts the app already holds, so the
 * rate-listing endpoints never have to query permit history (PB-2548).
 *
 * ## Invariants:
 * - Returns `unknown` — not `eligible` — while a signed-in customer's garage accounts are still
 *   loading, so the caller withholds the rate instead of briefly offering it.
 *   Why: showing then removing it is worse than a short delay, and guessing `eligible` would
 *   flash a promo the customer may not qualify for.
 * - A *failed* garage-accounts fetch is also `unknown`, never `eligible`.
 *   Why: an empty list from a dead call is indistinguishable from a customer with no history,
 *   and defaulting to `eligible` means every transient failure hands the promo to customers who
 *   do not qualify — silently, because nothing looks broken. This is not hypothetical: a stale
 *   local client made the call throw before it left the browser, the store still reported
 *   "loaded", and every ineligible customer saw the rate (PB-2548).
 * - This is presentation only. The api re-checks eligibility when the permit is actually bought,
 *   so a bypassed or stale UI cannot obtain the rate.
 *
 * @param garageId      the garage the rate belongs to
 * @param thresholdDays the rate's configured "days since last permit"; missing or below 1 means the
 *                      rate is not properly configured as an intro rate, so it is offered as normal
 * @param ctx           auth + garage-account state from the auth store
 */
export function resolveIntroRateEligibility(
  garageId: string | undefined,
  thresholdDays: number | undefined,
  ctx: EligibilityContext
): IntroRateEligibility {
  // Not a configured intro rate — nothing to gate.
  if (!thresholdDays || thresholdDays < 1) return 'eligible';

  // Anonymous visitors always see intro rates; identity resolves at sign-in (FR-3).
  if (!ctx.isAuthenticated) return 'eligible';

  // Signed in but we do not know their history yet — still loading, or the fetch failed and the
  // empty list tells us nothing about this customer.
  if (!ctx.hasLoadedGarageAccounts || ctx.garageAccountsLoadFailed) return 'unknown';

  if (!garageId) return 'eligible';

  const latestEnd = latestPermitEndAtGarage(garageId, ctx.garageAccounts ?? []);
  if (latestEnd === null) return 'eligible';

  const daysSince = Math.floor((Date.now() - latestEnd) / MS_PER_DAY);
  return daysSince >= thresholdDays ? 'eligible' : 'ineligible';
}

/**
 * The intro-rate fields a rate payload may carry. Declared here because the generated API client
 * lags the api: `PermitRateResult` / `PermitRateOutput` gain these fields when the client is
 * regenerated from the current spec, but coupon rate types don't declare them yet even though the
 * api already sends them. Callers holding such a type can narrow to this rather than reaching for
 * `any` — see the coupon paths in `useMonthlyRateSelection` / `useMonthlySignup`.
 */
export interface IntroRateFields {
  introRate?: boolean;
  introRateDaysThreshold?: number;
  garageId?: string;
}

/**
 * True when a rate should be withheld from the list — an intro rate the customer either cannot
 * have, or whose eligibility is not yet known.
 */
export function shouldHideIntroRate(
  rate: IntroRateFields,
  garageId: string | undefined,
  ctx: EligibilityContext
): boolean {
  if (rate.introRate !== true) return false;
  const eligibility = resolveIntroRateEligibility(
    rate.garageId ?? garageId,
    rate.introRateDaysThreshold,
    ctx
  );
  return eligibility !== 'eligible';
}
